import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { subscribeLeads } from '../../data/listeners/leads';
import {
  monthGrid, weekDays, weekNumber, groupByDay, dayKey, myDayTiles, dayBlocks,
} from '../../data/activity';
import ScopeBanner from '../../components/ScopeBanner';
import AgentFilter from '../../components/AgentFilter';
import NewLeadModal from '../../components/NewLeadModal';
import Icon from '../../components/Icon';
import { useUI } from '../../context/UIContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import MobileAgenda from '../../components/MobileAgenda';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// The four My Day tiles, in display order. `key` indexes into myDayTiles().
const TILES = [
  { key: 'overdue', label: 'Overdue', color: 'var(--presto-error)' },
  { key: 'today', label: 'Due today' },
  { key: 'service', label: 'Service visits due' },
  { key: 'touches', label: 'Touches logged today', title: 'Populates once visits are logged' },
];

// BUG-018: each tile is clickable and opens a filtered list of exactly those
// leads. Counts come straight from the same arrays the list renders, so a tile's
// number always equals its list length.
function StatCards({ tiles, active, onSelect }) {
  return (
    <div className="stat-row">
      {TILES.map((t) => (
        <div
          key={t.key}
          className={`stat${active === t.key ? ' active' : ''}`}
          role="button"
          tabIndex={0}
          aria-pressed={active === t.key}
          title={t.title}
          style={{ cursor: 'pointer' }}
          onClick={() => onSelect(t.key)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(t.key); } }}
        >
          <div className="n" style={t.color ? { color: t.color } : undefined}>{tiles[t.key].length}</div>
          <div className="l">{t.label}</div>
        </div>
      ))}
    </div>
  );
}

const TILE_LABEL = Object.fromEntries(TILES.map((t) => [t.key, t.label]));

function Event({ lead, onOpen }) {
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onOpen?.(lead.id); }}
      style={{ fontSize: 'var(--text-caption)', background: 'var(--presto-sunny-light)', color: 'var(--presto-suit)', borderRadius: 'var(--radius-sm)', padding: '1px 5px', marginTop: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', cursor: 'pointer' }}
    >
      {lead.businessName}
    </div>
  );
}

export default function MyDay() {
  const { tenantId, role, territoryIds } = useAuth();
  const { openLead, openLogVisit } = useUI();
  const [leads, setLeads] = useState([]);
  const [mode, setMode] = useState('month'); // day | week | month
  const [cursor, setCursor] = useState(() => new Date());
  const [ownerFilter, setOwnerFilter] = useState(''); // manager: '' = All team
  const [showNew, setShowNew] = useState(false);
  const [tile, setTile] = useState(null); // BUG-018: selected stat tile → filtered list

  const isMobile = useIsMobile();
  // On phones default to Day and never allow Month.
  useEffect(() => { if (isMobile && mode === 'month') setMode('day'); }, [isMobile, mode]);
  const MODES = isMobile ? ['day', 'week'] : ['day', 'week', 'month'];

  useEffect(() => subscribeLeads({ tenantId, role, territoryIds }, setLeads), [tenantId, role, territoryIds]);
  const view = useMemo(
    () => (ownerFilter ? leads.filter((l) => l.ownerUid === ownerFilter) : leads),
    [leads, ownerFilter],
  );
  const tiles = useMemo(() => myDayTiles(view), [view]);
  const agenda = useMemo(() => dayBlocks(view, cursor.getTime()), [view, cursor]);
  const byDay = useMemo(() => groupByDay(view), [view]);
  const leadsOn = (d) => byDay[dayKey(d.getTime?.() ?? d)] ?? [];

  const shift = (n) => {
    const d = new Date(cursor);
    if (mode === 'month') d.setMonth(d.getMonth() + n);
    else if (mode === 'week') d.setDate(d.getDate() + n * 7);
    else d.setDate(d.getDate() + n);
    setCursor(d);
  };

  const title = mode === 'month'
    ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : mode === 'week'
      ? `Week ${weekNumber(cursor)} · ${cursor.getFullYear()}`
      : cursor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  const todayKey = dayKey(Date.now());
  const now = new Date();
  const subtitle = `${now.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })} · Week ${weekNumber(now)}`;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>My Day</h1>
          <div className="sub">{subtitle}</div>
        </div>
        <span className="spacer" />
        <AgentFilter value={ownerFilter} onChange={setOwnerFilter} />
        <div className="seg">
          {MODES.map((m) => (
            <button key={m} className={mode === m ? 'on' : ''} onClick={() => setMode(m)}>{m[0].toUpperCase() + m.slice(1)}</button>
          ))}
        </div>
        <button className="btn btn-ghost" onClick={() => setShowNew(true)}><Icon name="plus" size={15} /> New lead</button>
        <button className="btn btn-primary" onClick={() => openLogVisit()}><Icon name="logvisit" size={15} /> Log Touchpoint</button>
      </div>

      <ScopeBanner />
      <StatCards tiles={tiles} active={tile} onSelect={(k) => setTile((cur) => (cur === k ? null : k))} />

      {tile && (
        <div className="card card-pad tile-list" style={{ marginTop: 'var(--space-3)' }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
            <b>{TILE_LABEL[tile]} <span className="muted">({tiles[tile].length})</span></b>
            <button className="btn btn-ghost btn-sm" onClick={() => setTile(null)}>✕ Clear</button>
          </div>
          {tiles[tile].length === 0 ? (
            <div className="muted" style={{ fontSize: 'var(--text-small)' }}>Nothing here right now.</div>
          ) : (
            tiles[tile].map((l) => (
              <div
                key={l.id}
                className="tile-list-row"
                onClick={() => openLead(l.id)}
                style={{ padding: '8px 0', borderBottom: 'var(--border-thin) solid var(--color-border)', cursor: 'pointer', fontSize: 'var(--text-small)' }}
              >
                <b>{l.businessName}</b> · {l.stage} · {l.category}
              </div>
            ))
          )}
        </div>
      )}

      <div className="chips" style={{ alignItems: 'center' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => shift(-1)}>‹</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setCursor(new Date())}>Today</button>
        <button className="btn btn-ghost btn-sm" onClick={() => shift(1)}>›</button>
        <b style={{ marginLeft: 8 }}>{title}</b>
      </div>

      {isMobile ? (
        <MobileAgenda
          mode={mode === 'month' ? 'day' : mode}
          cursor={cursor}
          leadsOn={leadsOn}
          shift={shift}
          onOpen={openLead}
          onLogVisit={(id) => openLogVisit(id)}
        />
      ) : (
        <>
          {mode === 'month' && (
            <div className="table-wrap" style={{ padding: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '32px repeat(7, 1fr)', gap: 4 }}>
                <div />
                {DOW.map((d) => <div key={d} style={{ textAlign: 'center', fontSize: 9, textTransform: 'uppercase', color: 'var(--color-fg-faint)', fontWeight: 600 }}>{d}</div>)}
                {monthGrid(cursor.getFullYear(), cursor.getMonth()).map((row, ri) => (
                  <RowFragment key={ri} row={row} leadsOn={leadsOn} todayKey={todayKey} onOpen={openLead} />
                ))}
              </div>
            </div>
          )}

          {mode === 'week' && (
            <div className="table-wrap" style={{ padding: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '32px repeat(7, 1fr)', gap: 4 }}>
                <div style={{ display: 'grid', placeItems: 'center', color: 'var(--color-fg-faint)', fontSize: 10 }}>W{weekNumber(cursor)}</div>
                {weekDays(cursor).map((d) => (
                  <div key={d.toISOString()} style={{ minHeight: 160, border: 'var(--border-thin) solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 6, boxShadow: dayKey(d.getTime()) === todayKey ? 'inset 0 0 0 2px var(--color-brand)' : 'none' }}>
                    <div style={{ fontSize: 'var(--text-caption)', color: 'var(--color-fg-muted)' }}>{DOW[d.getDay()]} {d.getDate()}</div>
                    {leadsOn(d).map((l) => <Event key={l.id} lead={l} onOpen={openLead} />)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {mode === 'day' && (
            <div className="day-agenda">
              {[
                { key: 'overdue', title: 'Overdue', items: agenda.overdue, color: 'var(--presto-error)' },
                { key: 'today', title: 'Due today', items: agenda.today, color: 'var(--presto-suit)' },
                { key: 'later', title: 'Later this week', items: agenda.later, color: 'var(--color-fg-muted)' },
              ].map((b) => (
                <div key={b.key} className="card card-pad day-block" style={{ marginBottom: 'var(--space-4)' }}>
                  <div className="section-title" style={{ marginTop: 0, color: b.color }}>
                    {b.title} <span className="badge tier-Mid">{b.items.length}</span>
                  </div>
                  {b.items.length === 0 ? (
                    <div className="muted" style={{ fontSize: 'var(--text-small)' }}>Nothing here.</div>
                  ) : (
                    b.items.map((l) => (
                      <div
                        key={l.id}
                        className="agenda-row"
                        onClick={() => openLead(l.id)}
                        style={{ padding: '8px 0', borderBottom: 'var(--border-thin) solid var(--color-border)', fontSize: 'var(--text-small)', cursor: 'pointer' }}
                      >
                        <b>{l.businessName}</b> · {l.stage} · {l.category}
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <p className="muted" style={{ marginTop: 8, fontSize: 'var(--text-caption)' }}>
        Week starts Sunday. Gold = follow-up · the slim left column is the week number (from Jan 1). Today is outlined.
      </p>

      {showNew && <NewLeadModal onClose={() => setShowNew(false)} />}
    </div>
  );
}

function RowFragment({ row, leadsOn, todayKey, onOpen }) {
  return (
    <>
      <div style={{ display: 'grid', placeItems: 'center', color: 'var(--color-fg-faint)', fontSize: 10 }}>W{row[0].week}</div>
      {row.map((cell) => (
        <div
          key={cell.date.toISOString()}
          style={{
            minHeight: 84, border: 'var(--border-thin) solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 4,
            opacity: cell.inMonth ? 1 : 0.4,
            boxShadow: dayKey(cell.date.getTime()) === todayKey ? 'inset 0 0 0 2px var(--color-brand)' : 'none',
          }}
        >
          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--color-fg-muted)' }}>{cell.date.getDate()}</div>
          {leadsOn(cell.date).slice(0, 3).map((l) => <Event key={l.id} lead={l} onOpen={onOpen} />)}
        </div>
      ))}
    </>
  );
}
